// React and React Router
import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import RequireAuth from "../common/routing/RequireAuth";

// ReactGA

//axios
import axios from "axios";

// Font AWesome
import("./FontAwesomeLoader").then((FontAwesomeLoader) => {
  FontAwesomeLoader.default();
});

// css
import "../scss/reset.scss";
import "../scss/base.scss";

// Swiper
import "swiper/css";
// import "swiper/css/bundle";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/thumbs";

// Default Component
import Header from "../common/nav/Header";
import SwitchFooter from "../common/SwitchFooter";
import ErrorBoundary from "../common/ErrorBoundary";

// Pages
import LoginPageContainer from "../features/login/LoginPageContainer";
import RegisterPage from "../features/registration/RegisterPage";
const TopPage = React.lazy(() => import("../features/top/TopPage"));
const Legalese = React.lazy(() => import("../features/legalese/Legalese"));
import ArchivePerson from "../features/person-db/ArchivePerson";
import ArchiveTeam from "../features/team-db/ArchiveTeam";
import ArchiveTopics from "../features/topics-db/ArchiveTopics";
import ArchiveEvent from "../features/event-db/ArchiveEvent";
import Management from "../features/management/Management";
import ErrorPage from "../features/404page/ErrorPage";
import ScrollToTop from "../common/routing/ScrollToTop";
import ManagementJikei from "../features/management-jikei/ManagementJikei";
import PasswordReminder from "../features/password_reminder/PasswordReminder";
import ResendVerificationCodePage from "../features/registration/ResendVerficationCodeContainer";
import VerificationPage from "../features/registration/VerificationPage";
import ArchiveProject from "../features/project-list/ArchiveProject";
import ContactPageContainer from "../features/contact/ContactPageContainer";
import MyPageContainer from "../features/mypage/MyPageContainer";
import SelectCup from "../features/selectcup/SelectCup";
import GameRelatedListTermContainer from "../features/game-search/GameRelatedListTermContainer";
import FacilityListContainer from "../features/facility/FacilityListContainer";
import FacilityDetailContainer from "../features/facility/FacilityDetailContainer";
import FacilityTopContainer from "../features/facility/top/FacilityTopContainer";

import LoadingSpinner from "../common/LoadingSpinner";
import TutorialModal from "../common/TutorialModal";
import ChatProvider from "../features/chat/ChatProvider";
import SearchWrapper from "../common/search-wrapper/SearchWrapper";

// Redux
import { Provider } from "react-redux";
import { store } from "../redux/store";

// Auth
import readAuthCookie from "../utils/readAuthCookie";
import {
  AuthStatus,
  checkAuth,
  clearAuthInfo,
  setAuthState,
} from "../redux/reducers/auth";

// i18n
import "../i18n";

import { useQuery } from "../utils/GeneralUtils";
import UserDetailContainer from "../features/user-detail/UserDetailContainer";
import LandingPage from "../features/lp/LandingPage";
import DocumentationPage from "../features/documentation/DocumentationPage";
import CorrectPageContainer from "../common/routing/CorrectPageContainer";

if (process.env.NODE_ENV !== "production") {
  console.log("NOTICE: Currently in development mode.");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ネットワークの再接続や離れたフォーカスが再度当たった時のバックグラウンドフェッチを抑制
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <AppRoute />
      </Router>
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

export const AppRoute: React.FC = () => {
  // useTracking("UA-156199175-2");
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (
        error &&
        (error.response.data.message.error == "003" ||
          error.response.data.message.error == "004" ||
          error.response.data.message.error == "005" ||
          error.response.data.message.error == "016")
      ) {
        store.dispatch(clearAuthInfo());
      }

      return Promise.reject(error);
    }
  );

  const query = useQuery();

  const [isIconsLoaded, setIsIconsLoaded] = useState(false);

  useEffect(() => {
    const cur_auth_cookie = readAuthCookie();

    if (cur_auth_cookie !== undefined) {
      store.dispatch(checkAuth(cur_auth_cookie));
    } else {
      store.dispatch(setAuthState(AuthStatus.NotAuthenticated));
    }

    setTimeout(() => setIsIconsLoaded(true), 100);
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ChatProvider>
          {query.get("showTutorial") ? <TutorialModal /> : null}
          <ScrollToTop />
          {isIconsLoaded ? <Header /> : null}
          <Routes>
            <Route path="/" element={<CorrectPageContainer />}>
              {/* Public Routes */}
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner space={6} />}>
                    <TopPage />
                  </Suspense>
                }
              />
              <Route path="login" element={<LoginPageContainer />} />

              <Route path="lp" element={<LandingPage />} />

              <Route path="documentation" element={<DocumentationPage />} />

              <Route path="password-reminder" element={<PasswordReminder />} />

              <Route path="select-cup" element={<SelectCup />} />

              <Route path="contact" element={<ContactPageContainer />} />

              <Route path="person/*" element={<ArchivePerson />} />

              <Route path="team/*" element={<ArchiveTeam />} />

              <Route path="project/*" element={<ArchiveProject />} />

              <Route path="topics/*" element={<ArchiveTopics />} />

              <Route path="event/*" element={<ArchiveEvent />} />

              <Route path="register" element={<RegisterPage />} />

              <Route path="verify" element={<VerificationPage />} />

              <Route
                path="verify/resend"
                element={<ResendVerificationCodePage />}
              />

              <Route
                path="info/*"
                element={
                  <Suspense fallback={<LoadingSpinner space={6} />}>
                    <Legalese />
                  </Suspense>
                }
              />

              <Route path="user/:id" element={<UserDetailContainer />} />

              <Route
                path="games/:id"
                element={<GameRelatedListTermContainer />}
              />

              <Route path="facility">
                <Route index Component={FacilityTopContainer} />
                <Route
                  path="list"
                  element={
                    <SearchWrapper wrappedComponent={FacilityListContainer} />
                  }
                />
                <Route path=":id" element={<FacilityDetailContainer />} />
              </Route>

              {/* Secure Routes */}
              <Route
                path="mypage/*"
                element={<RequireAuth component={MyPageContainer} />}
              />

              <Route
                path="admin/*"
                element={<RequireAuth component={Management} role="admin" />}
              />

              <Route
                path="jikei/*"
                element={
                  <RequireAuth
                    component={ManagementJikei}
                    role={["jikei_admin", "jikei_manager"]}
                  />
                }
              />
            </Route>

            {/* System Routes */}
            <Route path="/tips" element={<Navigate to="/topics" replace />} />

            <Route path="/trivia" element={<Navigate to="/topics" replace />} />

            <Route path="/404" element={<ErrorPage />} />

            <Route path="*" element={<ErrorPage />} />
          </Routes>
          {isIconsLoaded ? <SwitchFooter /> : null}
        </ChatProvider>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
